import { useEffect, useState } from "react";
import BaseModal from "./BaseModal";
import Icon1 from "@assets/icons/Icon";
import { PartnerInfos } from "types";
import { planningsApi } from "@services/api/plannings";
import { useDataStore } from "@store/user_org";
import { navigateTo } from "context/navigation.service";
import SmallSpinner from "@components/ui/loading/SmallSpinner";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";

interface ChargeMonth {
  month: number;
  year: number;
  label: string;
  projects: { project_id: string; project_name: string; conductor_name: string }[];
}

interface Props {
  partner: PartnerInfos;
  onClose: () => void;
  onEdit: () => void;
}

export default function PartnerWorkloadModal({ partner, onClose, onEdit }: Props) {
  const orgData = useDataStore((state) => state.orgData);
  const [months, setMonths] = useState<ChargeMonth[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!orgData?.id || !partner.id) return;
    setLoading(true);
    planningsApi
      .getChargeArtisanByPartner({ org_id: orgData.id, partner_id: partner.id })
      .then((res: any) => {
        setMonths(res.months || []);
        // Auto-expand all months
        const keys = new Set<string>((res.months || []).map((m: ChargeMonth) => `${m.year}-${m.month}`));
        setExpandedMonths(keys);
      })
      .catch(() => setMonths([]))
      .finally(() => setLoading(false));
  }, [orgData?.id, partner.id]);

  const toggleMonth = (key: string) => {
    setExpandedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleProjectClick = (projectId: string) => {
    onClose();
    navigateTo({ route: `/project/${projectId}` });
  };

  const partnerName = partner.company_name || partner.display_name || "Artisan";

  return (
    <BaseModal
      title={partnerName}
      startIcon={<Icon1 name="date" size={20} />}
      onClose={onClose}
    >
      <div className="flex flex-col h-full">
        <div className="p-4 flex flex-col gap-3 overflow-y-auto flex-1">
          {loading && (
            <div className="flex justify-center py-8">
              <SmallSpinner />
            </div>
          )}

          {!loading && months.length === 0 && (
            <div className="text-neutral-500 text-center py-8">
              Aucun démarrage prévu
            </div>
          )}

          {!loading &&
            months.map((m) => {
              const key = `${m.year}-${m.month}`;
              const isExpanded = expandedMonths.has(key);
              return (
                <div key={key} className="border border-neutral-200 rounded-xl overflow-hidden">
                  <button
                    onClick={() => toggleMonth(key)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-neutral-50 transition-colors"
                  >
                    <span className="font-medium text-h3">
                      {m.label} — {m.projects.length} démarrage{m.projects.length > 1 ? "s" : ""}
                    </span>
                    <Icon1
                      name={isExpanded ? "chevron-up" : "chevron-down"}
                      size={16}
                    />
                  </button>
                  {isExpanded && (
                    <div className="border-t border-neutral-100">
                      {m.projects.map((p) => (
                        <button
                          key={p.project_id}
                          onClick={() => handleProjectClick(p.project_id)}
                          className="w-full text-left px-4 py-2.5 hover:bg-neutral-50 transition-colors flex flex-col"
                        >
                          <span className="text-h4 font-medium text-neutral-900">
                            {p.project_name}
                          </span>
                          {p.conductor_name && (
                            <span className="text-small text-neutral-500">
                              CDT : {p.conductor_name}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
        <div className="sticky bottom-0 flex flex-row gap-2 justify-end p-4 border-t border-neutral-200 bg-white flex-shrink-0 rounded-b-2xl">
          <SecondaryButton label="Modifier" onClick={onEdit} variant="small" />
          <SecondaryButton label="Fermer" onClick={onClose} variant="small" />
        </div>
      </div>
    </BaseModal>
  );
}
